import React from 'react';
import BlogPost from '../../../components/BlogPost';

const VulnerabilityManagementSystemRuntime = () => (
  <BlogPost
    breadcrumbItems={[
      { path: '/', label: 'Home' },
      { path: '/blog', label: 'Blog' },
      { path: '/blog/cloud-security-series', label: 'Cloud Security Series' },
      {
        path: '/blog/cloud-security-series/vulnerability-management-system-runtime',
        label: 'Vulnerability Management System – Part 3: Scanning at Runtime',
      },
    ]}
    title="Vulnerability Management System – Part 3: Scanning at Runtime"
    date="Jan 10, 2025"
    content={
      <>
        <section className="blog-post__section">
          <h2>Vulnerability Scanning at Runtime</h2>
          <p>
            Cloud Infrastructure Security Series #8. 🎯 Vulnerability Management System (“VMS”).
            Part 3: Scanning at Runtime.
          </p>
          <p>
            In the original post we defined steps to implement a bullet-proof Cloud Infrastructure
            Security Posture. Now it's time to dive into the topic of{' '}
            <strong>🎯 Vulnerability Management</strong>. See Part 1: Hosts and Part 2: Containers
            to learn about hardening pipelines for AMIs and container images.
          </p>
          <p>
            <strong>❓ How would you approach the implementation of the following?</strong>
            <br />
            – Scan container images, applications, hosts for Vulnerabilities;
            <br />– Perform Regular Host and Container Security Patching and Version Upgrades;
          </p>
          <p>
            <strong>
              💪 Here is how, and while it's quite challenging, it is <em>very</em> rewarding from
              the cybersecurity perspective:
            </strong>
          </p>
          <p>
            <b>— Part 3. Scanning at Runtime —</b>
          </p>
          <ol>
            <li>
              Install an eXtended Detection and Response (XDR) platform such as{' '}
              <a href="https://www.wazuh.com" target="_blank" rel="noopener noreferrer">
                Wazuh
              </a>
              , or a professional grade network scanner such as Nessus Pro (
              <a href="https://lnkd.in/e__j-yTj" target="_blank" rel="noopener noreferrer">
                link
              </a>
              ).
            </li>
          </ol>
          <p>
            Interestingly, the XDR tool will represent a miniature SOC (Security Operations Center)
            inside your cloud environment, and – in addition to vulnerability scanning of the
            running hosts and application containers – will provide great observability to the
            internal and externally sourced security events (such as: privilege escalation, port
            scanning, DDoS attacks, etc). This is how you achieve the "Threat Detection" piece of
            cloud infrastructure security. Beware, though, that to fully enjoy this part you will
            have to perform additional configuration in the SOC world known as "noise canceling",
            "event correlation", and "prioritization". We'll get to these in the future posts, while
            discussing Observability.
          </p>
          <p>Now, back to vulnerability scanning.</p>
          <p>
            I prefer Wazuh as it is open-source and free to use, while Nessus Pro from Tenable is a
            commercial product. At the same time, Wazuh is way harder to configure, but it also
            provides a <strong>continuous</strong> vulnerability scanning capability, and the
            closest matching Tenable product to that is Tenable.sc.
          </p>
          <ol start={2}>
            <li>
              Set up XDR agents on your running hosts to continuously feed security audit logs from
              your hosts and containers into the XDR.
            </li>
            <li>
              If using Nessus Pro, configure regular network and host scans. Wazuh will perform
              scans continuously.
            </li>
            <li>
              Make time in your calendar to visit your XDR dashboard every day, or every other day.
              On the hosts / containers scanning dashboards, look for the discovered CVEs and
              prioritize them based on your use case. Some alerts will be the obvious
              false-positives, but in general the information provided by the XDR is a treasure, as
              it helps you see the vulnerabilities that are showing up at runtime (which you cannot
              necessarily catch at the Golden Image build time), and take corresponding corrective
              actions.
            </li>
          </ol>
          <p>In the next post we will talk about regular security patching.</p>
        </section>
      </>
    }
    cta={{
      title: 'We Are Here to Help!',
      text: "Struggling to set up or simplify your SOC 2 compliance? Our expert team at DevSecOps, Inc. specializes in making compliance manageable and straightforward for lean startups. We've successfully helped numerous startups quickly and affordably achieve compliance without sacrificing agility.",
      buttonText: `Let's Talk`,
      buttonHref: '/contact-us',
    }}
    prevPost={{
      href: '/blog/cloud-security-series/vulnerability-management-system-containers',
      label: 'Vulnerability Management System – Part 2: Containers',
    }}
    nextPost={{
      href: '/blog/cloud-security-series/regular-security-patching',
      label: 'Next: Regular Security Patching',
    }}
  />
);

export default VulnerabilityManagementSystemRuntime;
